// NOTE: Some of these versions have corresponding constants in /render/lib/versions.js
export const MIN_VERSION_CONDITIONAL_VISIBILITY: string = '1.1.0';
export const MIN_VERSION_JSON_RENDER: string = '2.0.2';
export const MIN_VERSION_EXPANDED_TEXT_NODES: string = '3.0.0';

type SitePlan = {
  price: number;
  numCollaborators: number;
  numCollections: number;
  pageViews: number;
  maxItems?: number;
  planId: string;
  stripePlanName: string;
  currency: string;
  maxPages: number;
  metadata: {
    type: string;
  };
};

export const SITE_PLANS: {
  static: SitePlan;
  cms: SitePlan;
} = {
  static: {
    price: 1500,
    numCollaborators: 0,
    numCollections: 0,
    pageViews: 25000,
    planId: 'hosting-static01',
    stripePlanName: 'Basic Hosting',
    currency: 'usd',
    maxPages: 80,
    metadata: {
      type: 'static',
    },
  },
  cms: {
    price: 2000,
    numCollaborators: 1,
    numCollections: 20,
    pageViews: 25000,
    maxItems: 500,
    planId: 'hosting-cms01',
    stripePlanName: 'CMS Hosting',
    currency: 'usd',
    maxPages: 80,
    metadata: {
      type: 'cms',
    },
  },
};

type StripePlan = {
  price: number;
  brandingPrice: number;
  interval: string;
  planId: string;
  stripePlanName: string;
  currency: string;
};

type StripePlans = {
  free: StripePlan;
  micro: StripePlan;
  personal: StripePlan;
  professional: StripePlan;
};

type SiteStripePlans = {
  static: StripePlans;
  cms: StripePlans;
};

export const SITE_STRIPE_PLANS: SiteStripePlans = {
  static: {
    free: {
      price: 1500,
      brandingPrice: 1000,
      interval: 'month',
      planId: 'hosting-static01-free',
      stripePlanName: 'Basic Hosting',
      currency: 'usd',
    },
    micro: {
      price: 1500,
      brandingPrice: 1000,
      interval: 'month',
      planId: 'hosting-static01-micro',
      stripePlanName: 'Basic Hosting (Micro)',
      currency: 'usd',
    },
    personal: {
      price: 500,
      brandingPrice: 1000,
      interval: 'month',
      planId: 'hosting-static01-personal',
      stripePlanName: 'Basic Hosting (Personal)',
      currency: 'usd',
    },
    professional: {
      price: 500,
      brandingPrice: 500,
      interval: 'month',
      planId: 'hosting-static01-professional',
      stripePlanName: 'Basic Hosting (Professional)',
      currency: 'usd',
    },
  },
  cms: {
    free: {
      price: 2000,
      brandingPrice: 1000,
      interval: 'month',
      planId: 'hosting-cms01-free',
      stripePlanName: 'CMS Hosting',
      currency: 'usd',
    },
    micro: {
      price: 2000,
      brandingPrice: 1000,
      interval: 'month',
      planId: 'hosting-cms01-micro',
      stripePlanName: 'CMS Hosting (Micro)',
      currency: 'usd',
    },
    personal: {
      price: 1000,
      brandingPrice: 1000,
      interval: 'month',
      planId: 'hosting-cms01-personal',
      stripePlanName: 'CMS Hosting (Personal)',
      currency: 'usd',
    },
    professional: {
      price: 1000,
      brandingPrice: 500,
      interval: 'month',
      planId: 'hosting-cms01-professional',
      stripePlanName: 'CMS Hosting (Professional)',
      currency: 'usd',
    },
  },
};

// TODO: Remove this:
export const SITE_STRIPE_YEARLY_PLANS: SiteStripePlans = {
  static: {
    free: {
      price: 1500 * 12,
      brandingPrice: 1000,
      interval: 'year',
      planId: 'hosting-static01-year-free',
      stripePlanName: 'Yearly Basic Hosting',
      currency: 'usd',
    },
    micro: {
      price: 1500 * 12,
      brandingPrice: 1000,
      interval: 'year',
      planId: 'hosting-static01-year-micro',
      stripePlanName: 'Yearly Basic Hosting (Micro)',
      currency: 'usd',
    },
    personal: {
      price: 500 * 12,
      brandingPrice: 1000,
      interval: 'year',
      planId: 'hosting-static01-year-personal',
      stripePlanName: 'Yearly Basic Hosting (Personal)',
      currency: 'usd',
    },
    professional: {
      price: 500 * 12,
      brandingPrice: 500,
      interval: 'year',
      planId: 'hosting-static01-year-professional',
      stripePlanName: 'Yearly Basic Hosting (Professional)',
      currency: 'usd',
    },
  },
  cms: {
    free: {
      price: 2000 * 12,
      brandingPrice: 1000,
      interval: 'year',
      planId: 'hosting-cms01-year-free',
      stripePlanName: 'Yearly CMS Hosting',
      currency: 'usd',
    },
    micro: {
      price: 2000 * 12,
      brandingPrice: 1000,
      interval: 'year',
      planId: 'hosting-cms01-year-micro',
      stripePlanName: 'Yearly CMS Hosting (Micro)',
      currency: 'usd',
    },
    personal: {
      price: 1000 * 12,
      brandingPrice: 1000,
      interval: 'year',
      planId: 'hosting-cms01-year-personal',
      stripePlanName: 'Yearly CMS Hosting (Personal)',
      currency: 'usd',
    },
    professional: {
      price: 1000 * 12,
      brandingPrice: 500,
      interval: 'year',
      planId: 'hosting-cms01-year-professional',
      stripePlanName: 'Yearly CMS Hosting (Professional)',
      currency: 'usd',
    },
  },
};

type AdditionalPrice =
  | {
      mode: 'tier';
      price: number;
      forEach: number;
      after: number;
    }
  | {
      mode: 'bool';
      price: number;
    };

type AdditionalPrices = {
  pageViews: AdditionalPrice;
  cmsItems: AdditionalPrice;
  collaborators: AdditionalPrice;
  formSubmissions: AdditionalPrice;
  customBranding: AdditionalPrice;
};

export const ADDITIONAL_PRICES: AdditionalPrices = {
  pageViews: {
    mode: 'tier',
    price: 1000,
    forEach: 100000,
    after: 100000,
  },
  cmsItems: {
    mode: 'tier',
    price: 1000,
    forEach: 500,
    after: 500,
  },
  collaborators: {
    mode: 'tier',
    price: 600,
    forEach: 1,
    after: 1,
  },
  formSubmissions: {
    mode: 'tier',
    price: 200,
    forEach: 100,
    after: 100,
  },
  customBranding: {
    mode: 'bool',
    price: 1000, // Overridden by plan's branding price.
  },
};

type PlanDiscount = {
  static: number;
  cms: number;
};

type DynamoPlan = {
  price: {
    month: number;
    year: number;
    forms: number;
    customDomain: number;
  };
  numFreeFormEntries: number;
  maxPages: number;
  maxSites: number;
  maxPublicSites: number;
  numExports?: number;
  canCustomFont: boolean;
  canSetTypeKit: boolean;
  numDomains: number;
  numBackups?: number;
  stripeIdPre?: string;
  name?: string;
  hostingDiscount?: PlanDiscount;
};

type DynamoPlans = {
  free: DynamoPlan;
  micro: DynamoPlan;
  personal: DynamoPlan;
  professional: DynamoPlan;
};

export const DYNAMO_PLANS: DynamoPlans = {
  free: {
    price: {
      month: 0,
      year: 0,
      forms: 2,
      customDomain: 0,
    },
    numFreeFormEntries: 500,
    maxPages: 2,
    maxSites: 2,
    maxPublicSites: 5,
    numExports: 0,
    canCustomFont: true,
    canSetTypeKit: true,
    numDomains: 0,
    numBackups: 20,
  },
  micro: {
    price: {
      month: 1600,
      year: 1400 * 12,
      forms: 2,
      customDomain: 0,
    },
    numFreeFormEntries: 500,
    maxPages: 100,
    maxSites: 1,
    maxPublicSites: 20,
    numDomains: 1,
    canCustomFont: true,
    canSetTypeKit: false,
    stripeIdPre: 'micro',
    name: 'Micro',
  },
  personal: {
    price: {
      month: 2000,
      year: 1600 * 12,
      forms: 1,
      customDomain: 499,
    },
    numFreeFormEntries: 500,
    maxPages: 100,
    maxSites: 20,
    maxPublicSites: 100,
    numDomains: 0,
    canCustomFont: true,
    canSetTypeKit: true,
    stripeIdPre: 'personal',
    name: 'Personal',
    hostingDiscount: {
      static: 66 / 100,
      cms: 50 / 100,
    },
  },
  professional: {
    price: {
      month: 4200,
      year: 3500 * 12,
      forms: 1,
      customDomain: 499,
    },
    numFreeFormEntries: 500,
    maxPages: 100,
    maxSites: 100,
    maxPublicSites: 100,
    numDomains: 0,
    canCustomFont: true,
    canSetTypeKit: true,
    stripeIdPre: 'professional',
    name: 'Professional',
    hostingDiscount: {
      static: 66 / 100,
      cms: 50 / 100,
    },
  },
};

// assuming static hosting is $15, static custom domains should cost $5 for orgs
export const ORG_DISCOUNT: PlanDiscount = DYNAMO_PLANS.professional
  .hostingDiscount || {
  static: 66 / 100,
  cms: 50 / 100,
};

type OrgPlan = {
  seats: number;
  maxTemplates: number;
  maxSites: number;
  maxPages: number;
  price: {
    month: number;
    year: number;
    customDomain: number;
  };
  stripeIdPre: string;
  name: string;
};

type OrgPlans = {
  small: OrgPlan;
  medium: OrgPlan;
  large: OrgPlan;
};

export const ORG_PLANS: OrgPlans = {
  small: {
    seats: 2,
    maxTemplates: 3,
    maxSites: 100,
    maxPages: 100,
    price: {
      month: 8400,
      year: 7800 * 12,
      customDomain: 499,
    },
    stripeIdPre: 'team-small',
    name: 'Team Small',
  },
  medium: {
    seats: 4,
    maxTemplates: 6,
    maxSites: 200,
    maxPages: 100,
    price: {
      month: 16800,
      year: 15600 * 12,
      customDomain: 499,
    },
    stripeIdPre: 'team-medium',
    name: 'Team Medium',
  },
  large: {
    seats: 8,
    maxTemplates: 12,
    maxSites: 400,
    maxPages: 100,
    price: {
      month: 33600,
      year: 31500 * 12,
      customDomain: 499,
    },
    stripeIdPre: 'team-large',
    name: 'Team Large',
  },
};
